import { useState } from 'react';

interface UseAssignmentCloseConfirmationParams {
  isFormDirty: boolean;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (open: boolean) => void;
  onClose: () => void;
}

/**
 * Manages the close-confirmation flow for the assignment modal.
 *
 * When the user tries to close with unsaved changes, a confirmation dialog is shown.
 * If the date picker is open, it is closed first instead of triggering the confirm dialog.
 */
export function useAssignmentCloseConfirmation({
  isFormDirty,
  isDatePickerOpen,
  setIsDatePickerOpen,
  onClose,
}: UseAssignmentCloseConfirmationParams) {
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Only close the dialog — the modal's open-effect resets the form on every open,
  // so there's no need to reset here.
  function handleClose() {
    onClose();
  }

  function handleRequestClose() {
    if (isDatePickerOpen) {
      setIsDatePickerOpen(false);
      return;
    }
    if (isFormDirty) setShowConfirmClose(true);
    else handleClose();
  }

  function handleConfirmClose() {
    setShowConfirmClose(false);
    handleClose();
  }

  function handleCancelClose() {
    setShowConfirmClose(false);
  }

  return {
    showConfirmClose,
    handleRequestClose,
    handleConfirmClose,
    handleCancelClose,
    handleClose,
  };
}
